import type { Ref } from 'vue'

import { computed, readonly, ref, watch } from 'vue'

import userService from '@/services/userService'

import { useUser } from './useUser'

/**
 * Global admin mode state
 * Admin mode allows users with admin role to view all entities in the system
 * instead of only their own entities
 */
const adminModeEnabled: Ref<boolean> = ref(false)

/**
 * Composable for managing admin mode toggle
 * Admin mode is only available to users with the admin role
 * When enabled, shows all entities (events, groups, workspaces)
 * instead of just the user's own entities
 */
export function useAdminMode() {
  const { hasRole, userInfo, fetchUserInfo } = useUser()

  // Check if user has admin role
  const isAdminUser = computed(() => hasRole.value('admin'))

  // Admin mode can only be enabled if user has admin role
  const canUseAdminMode = computed(() => isAdminUser.value)

  // Watch for user role changes - disable admin mode if user loses admin role
  watch(isAdminUser, (newValue) => {
    if (!newValue && adminModeEnabled.value) {
      adminModeEnabled.value = false
    }
  })

  watch(
    () => userInfo.value?.adminModeEnabled,
    (newValue) => {
      adminModeEnabled.value = !!newValue
    },
    { immediate: true },
  )

  const syncAdminModeFromProfile = () => {
    adminModeEnabled.value = !!userInfo.value?.adminModeEnabled
  }

  const setAdminMode = async (enabled: boolean) => {
    const response = await userService.setAdminMode(enabled)
    adminModeEnabled.value = response.enabled
    await fetchUserInfo(true)
    syncAdminModeFromProfile()
  }

  /**
   * Toggle admin mode on/off
   * Only works if user has admin role
   */
  const toggleAdminMode = async () => {
    if (!canUseAdminMode.value) {
      console.warn('Admin mode can only be enabled by users with admin role')
      return
    }

    try {
      await setAdminMode(!adminModeEnabled.value)
    } catch (error) {
      console.error('Failed to toggle admin mode:', error)
      await fetchUserInfo(true)
      syncAdminModeFromProfile()
      throw error
    }
  }

  /**
   * Enable admin mode
   * Only works if user has admin role
   */
  const enableAdminMode = async () => {
    if (!canUseAdminMode.value) {
      console.warn('Admin mode can only be enabled by users with admin role')
      return
    }

    try {
      await setAdminMode(true)
    } catch (error) {
      console.error('Failed to enable admin mode:', error)
      await fetchUserInfo(true)
      syncAdminModeFromProfile()
      throw error
    }
  }

  /**
   * Disable admin mode
   */
  const disableAdminMode = async () => {
    if (!canUseAdminMode.value) {
      adminModeEnabled.value = false
      return
    }

    try {
      await setAdminMode(false)
    } catch (error) {
      console.error('Failed to disable admin mode:', error)
      await fetchUserInfo(true)
      syncAdminModeFromProfile()
      throw error
    }
  }

  return {
    // State
    adminModeEnabled: readonly(adminModeEnabled),
    canUseAdminMode,
    isAdminUser,

    // Actions
    toggleAdminMode,
    enableAdminMode,
    disableAdminMode,
  }
}
